import axios from 'axios'
import jwtDecode from 'jwt-decode'

let refreshRequest = null

function readStoredUser() {
  try {
    return JSON.parse(localStorage.getItem('user'))
  } catch (error) {
    return null
  }
}

export function clearAuthSession() {
  localStorage.removeItem('user')
  localStorage.removeItem('expires_at')
  localStorage.removeItem('setupTime')
}

export function storeRefreshedTokens(tokens) {
  const user = readStoredUser()
  if (!user || !tokens.access_token) return false

  user.access_token = tokens.access_token
  if (tokens.refresh_token) user.refresh_token = tokens.refresh_token
  localStorage.setItem('user', JSON.stringify(user))

  const decoded = jwtDecode(tokens.access_token)
  localStorage.setItem('expires_at', decoded.exp * 1000)
  return true
}

function refreshTokens() {
  if (refreshRequest) return refreshRequest

  const user = readStoredUser()
  if (!user?.refresh_token) return Promise.reject(new Error('Refresh token unavailable'))

  refreshRequest = axios.post(
    `${process.env.VUE_APP_ROOT_API}/auth/refresh`,
    {},
    { headers: { authorization: `Bearer ${user.refresh_token}` } }
  ).then(response => {
    if (!storeRefreshedTokens(response.data)) {
      throw new Error('Unable to store refreshed tokens')
    }
    return response.data.access_token
  }).finally(() => {
    refreshRequest = null
  })

  return refreshRequest
}

export default () => {
  const headers = { 'content-type': 'application/json' }
  const user = readStoredUser()
  if (user?.access_token) headers.authorization = `Bearer ${user.access_token}`

  const axiosInstance = axios.create({
    baseURL: process.env.VUE_APP_ROOT_API,
    headers,
  })

  axiosInstance.interceptors.response.use(
    response => response,
    async error => {
      const request = error.config
      if (error.response?.status !== 401 || !request || request._retry) {
        return Promise.reject(error)
      }

      request._retry = true
      try {
        const accessToken = await refreshTokens()
        request.headers = request.headers || {}
        request.headers.authorization = `Bearer ${accessToken}`
        return axiosInstance.request(request)
      } catch (refreshError) {
        clearAuthSession()
        window.dispatchEvent(new CustomEvent('auth:expired'))
        return Promise.reject(refreshError)
      }
    }
  )

  return axiosInstance
}
